import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCardIcon, PayPalIcon, ApplePayIcon, CheckCircleIcon, XIcon, CheckIcon, LockIcon } from './icons';
import { LicenseInfo, MarketplaceItem } from '../../types';
import { formatCurrency } from '../../i18n';

type PaymentMethod = 'stripe' | 'paypal' | 'applepay';

interface PaymentGatewayProps {
    item: {
        name: string;
        price: number;
        type: 'license' | 'marketplace';
        item: LicenseInfo | MarketplaceItem
    };
    onClose: () => void;
    onPaymentSuccess: () => void;
    currency: string;
    isDarkMode?: boolean;
}

const PaymentGateway: React.FC<PaymentGatewayProps> = ({ item, onClose, onPaymentSuccess, currency, isDarkMode = false }) => {
    const IS_DEV = import.meta.env.DEV;

    // Prevent fake payments from being reachable in production builds
    if (!IS_DEV) {
      return (
        <div className="p-4">
          <div className="text-lg font-semibold">Payments unavailable</div>
          <div className="text-sm opacity-80">
            This payment flow is disabled in production. Please use the marketplace checkout.
          </div>
          <button className="mt-3 px-4 py-2 rounded-xl" onClick={onClose}>
            Close
          </button>
        </div>
      );
    }
    const { t, i18n } = useTranslation();
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
    const [processingState, setProcessingState] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');

    const handlePayment = () => {
        if (!selectedMethod) return;

        setProcessingState('processing');
        setTimeout(() => {
            // Simulate a successful payment
            setProcessingState('success');
            setTimeout(() => {
                onPaymentSuccess();
            }, 1500); // Wait a bit on success screen before closing
        }, 2000); // Simulate network delay
    };

    const formattedAmount = formatCurrency(item.price, currency, i18n.language);

    const renderPaymentContent = () => {
        switch(processingState) {
            case 'processing':
                return (
                    <div className="flex flex-col items-center justify-center h-48">
                        <svg className="animate-spin h-10 w-10 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p className="mt-4 font-semibold text-slate-600 dark:text-slate-300">{t('payment.processing')}</p>
                    </div>
                );
            case 'success':
                 return (
                    <div className="flex flex-col items-center justify-center h-48 text-center">
                        <CheckCircleIcon className="w-16 h-16 text-secondary" />
                        <p className="mt-4 text-xl font-bold text-slate-700 dark:text-slate-200">{t('payment.successTitle')}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{t('payment.successSubtitle')}</p>
                    </div>
                );
            case 'failed':
                 return (
                     <div className="flex flex-col items-center justify-center h-48 text-center">
                        <XIcon className="w-16 h-16 text-danger" />
                        <p className="mt-4 text-xl font-bold text-slate-700 dark:text-slate-200">{t('payment.failedTitle')}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{t('payment.failedSubtitle')}</p>
                    </div>
                 )
            default: // idle
                return (
                    <>
                        <div>
                            <h3 className="font-semibold mb-3 text-slate-700 dark:text-slate-300">{t('payment.selectMethod')}</h3>
                            <div className="space-y-3">
                                <PaymentOption
                                    id="stripe"
                                    icon={<CreditCardIcon className="w-6 h-6" />}
                                    label={t('payment.card')}
                                    selected={selectedMethod === 'stripe'}
                                    onSelect={() => setSelectedMethod('stripe')}
                                />
                                <PaymentOption
                                    id="paypal"
                                    icon={<PayPalIcon className="w-6 h-6 text-blue-800 dark:text-blue-300" />}
                                    label="PayPal"
                                    selected={selectedMethod === 'paypal'}
                                    onSelect={() => setSelectedMethod('paypal')}
                                />
                                <PaymentOption
                                    id="applepay"
                                    icon={<ApplePayIcon className="w-6 h-6" />}
                                    label="Apple Pay"
                                    selected={selectedMethod === 'applepay'}
                                    onSelect={() => setSelectedMethod('applepay')}
                                />
                            </div>
                        </div>
                        <div className="text-center mt-4">
                            <div className="flex items-center justify-center space-x-2 text-xs text-slate-500 dark:text-slate-400">
                                <LockIcon className="w-4 h-4" />
                                <span>{t('payment.secure')}</span>
                            </div>
                        </div>
                        <button
                            onClick={handlePayment}
                            disabled={!selectedMethod || processingState !== 'idle'}
                            className="w-full mt-4 p-3 rounded-xl bg-[#7657E8] hover:bg-opacity-90 text-white font-bold shadow-md transition-all transform hover:scale-105 disabled:bg-slate-400 disabled:scale-100 disabled:cursor-not-allowed"
                        >
                            {t('payment.payButton', { amount: formattedAmount })}
                        </button>
                    </>
                );
        }
    };

    return (
        <div data-roddo-theme={isDarkMode ? 'dark' : 'light'} className={`${isDarkMode ? 'dark ' : ''}fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm`}>
            <div className="bg-[#FFFCF7] dark:bg-[#101727] rounded-[20px] shadow-xl w-full max-w-sm p-6 space-y-4 relative">
                <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-[#1B1830]">
                    <XIcon className="w-5 h-5 text-slate-500" />
                </button>
                <h2 className="text-xl font-extrabold text-center text-text-primary dark:text-text-on-dark">{t('payment.title')}</h2>
                
                <div className="p-4 bg-slate-100 dark:bg-[#1B1830]/50 rounded-xl text-center">
                    <p className="text-sm text-slate-600 dark:text-slate-300">{t('payment.purchasing')}</p>
                    <p className="font-bold text-lg text-text-primary dark:text-text-on-dark">{item.name}</p>
                    <p className="text-2xl font-extrabold text-primary dark:text-[#9278EC] mt-1">{formattedAmount}</p>
                </div>

                {renderPaymentContent()}
            </div>
        </div>
    );
};

interface PaymentOptionProps {
    id: PaymentMethod;
    icon: React.ReactNode;
    label: string;
    selected: boolean;
    onSelect: () => void;
}

const PaymentOption: React.FC<PaymentOptionProps> = ({ icon, label, selected, onSelect }) => (
    <button
        onClick={onSelect}
        className={`w-full flex items-center space-x-4 p-3 rounded-xl border-2 transition-colors ${selected ? 'border-[#7657E8] bg-[#7657E8]/10' : 'border-slate-200 dark:border-[#9F8CDC]/20 hover:border-slate-300 dark:hover:border-slate-600'}`}
    >
        {icon}
        <span className="font-semibold">{label}</span>
        <div className={`ml-auto w-5 h-5 rounded-full border-2 ${selected ? 'border-[#7657E8] bg-[#7657E8]' : 'border-slate-300 dark:border-[#9F8CDC]/24'}`}>
            {selected && <CheckIcon className="w-4 h-4 text-white" />}
        </div>
    </button>
);


export default PaymentGateway;